import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Typography, Space, Progress, Button, message } from 'antd';
import { ReloadOutlined, DatabaseOutlined, PlayCircleOutlined } from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import { backupService } from '../services/backups';
import { useAuth } from '../contexts/AuthContext';
import './Dashboard.css';

const { Title, Text } = Typography;

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${sizes[i]}`;
};

function Backup() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [totalBytes, setTotalBytes] = useState(0);
  const [quotaBytes, setQuotaBytes] = useState(5 * 1024 * 1024);
  const [triggering, setTriggering] = useState(false);

  const isAdmin = user?.role === 'admin';

  const loadBackups = async () => {
    setLoading(true);
    try {
      const data = await backupService.list();
      setBackups(data.backups || []);
      setTotalBytes(data.total_bytes || 0);
      setQuotaBytes(data.quota_bytes || 5 * 1024 * 1024);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore nel caricamento backup';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadBackups();
    }
  }, [isAdmin]);

  const percent = useMemo(() => {
    if (!quotaBytes) return 0;
    return Math.min(100, Math.round((totalBytes / quotaBytes) * 100));
  }, [totalBytes, quotaBytes]);

  const columns = [
    { title: 'Nome file', dataIndex: 'name', key: 'name' },
    { title: 'Dimensione', dataIndex: 'size_bytes', key: 'size', render: (v) => formatBytes(v) },
    { title: 'Creato il', dataIndex: 'created_at', key: 'created_at', render: (v) => new Date(v).toLocaleString('it-IT') },
  ];

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const res = await backupService.trigger();
      message.success(`Backup creato: ${res.filename}`);
      await loadBackups();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore nell\'esecuzione del backup';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setTriggering(false);
    }
  };

  if (!isAdmin) {
    return (
      <MainLayout title="Backup">
        <Card>
          <Text>Accesso non autorizzato.</Text>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Backup">
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>Backup</Title>
          <Space>
            <DatabaseOutlined style={{ color: '#1677ff', fontSize: 18 }} />
            <Text strong>Quota usata: {formatBytes(totalBytes)} / {formatBytes(quotaBytes)} ({percent}%)</Text>
            <Progress percent={percent} showInfo={false} style={{ width: 150 }} />
            <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleTrigger} loading={triggering}>
              Esegui backup ora
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadBackups} loading={loading}>
              Aggiorna
            </Button>
          </Space>
        </Space>
      </Card>

      <Card>
        <Table
          rowKey="name"
          dataSource={backups}
          columns={columns}
          loading={loading}
          pagination={false}
        />
      </Card>
    </MainLayout>
  );
}

export default Backup;
